import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { products } from '../../mocks/products';
import Button from '../../components/base/Button';

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = products.find(p => p.id === Number(id));

  const [selectedColor, setSelectedColor] = useState(product?.colors[0] || '');
  const [selectedSize, setSelectedSize] = useState(product?.sizes[0] || '');
  const [quantity, setQuantity] = useState(product?.minQuantity || 10);
  const [showSizeGuide, setShowSizeGuide] = useState(false);

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Producto no encontrado</h2>
          <Link to="/catalogo">
            <Button>Volver al Catálogo</Button>
          </Link>
        </div>
      </div>
    );
  }

  const totalPrice = (quantity * product.unitPrice).toFixed(2);

  // Generar imagen según el color seleccionado
  const getColorImage = (color: string) => {
    const colorMap: Record<string, string> = {
      'Blanco': 'white',
      'Negro': 'black',
      'Gris plomo': 'grey',
      'Plomo': 'grey',
      'Amarillo': 'yellow',
      'Naranja': 'orange',
      'Lima': 'lime green',
      'Rojo': 'red',
      'Azul Marino': 'navy blue',
      'Azul Royal': 'royal blue',
      'Verde': 'green',
      'Verde Oliva': 'olive green',
      'Gris': 'grey',
      'Gris Oscuro': 'dark grey',
      'Burdeos': 'burgundy'
    };
    
    const colorName = colorMap[color] || color.toLowerCase();
    const productType = product.category.toLowerCase();
    
    return `https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7BproductType%7D%20$%7BcolorName%7D%20color%20on%20white%20background%20professional%20product%20photography%20clean%20studio%20lighting%20modern%20style%20folded%20display&width=600&height=600&seq=${product.id}${color}&orientation=squarish`;
  };

  const handleAddToCart = () => {
    const cartItem = {
      productId: product.id,
      name: product.name,
      color: selectedColor,
      size: selectedSize,
      quantity: quantity,
      unitPrice: product.unitPrice,
      totalPrice: parseFloat(totalPrice),
      image: getColorImage(selectedColor)
    };

    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
    existingCart.push(cartItem);
    localStorage.setItem('cart', JSON.stringify(existingCart));

    navigate('/checkout');
  };

  // Guía de tallas según el tipo de producto
  const getSizeGuide = () => {
    const category = product.category.toLowerCase();
    
    if (category.includes('camiseta')) {
      return {
        title: 'Guía de Tallas - Camisetas',
        sizes: [
          { size: 'S', chest: '46 cm', length: '69 cm', shoulder: '42 cm' },
          { size: 'M', chest: '51 cm', length: '72 cm', shoulder: '44 cm' },
          { size: 'L', chest: '56 cm', length: '75 cm', shoulder: '46 cm' },
          { size: 'XL', chest: '61 cm', length: '78 cm', shoulder: '48 cm' }
        ]
      };
    } else if (category.includes('polo')) {
      return {
        title: 'Guía de Tallas - Polos',
        sizes: [
          { size: 'S', chest: '48 cm', length: '70 cm', shoulder: '43 cm' },
          { size: 'M', chest: '52 cm', length: '73 cm', shoulder: '45 cm' },
          { size: 'L', chest: '56 cm', length: '76 cm', shoulder: '47 cm' },
          { size: 'XL', chest: '60 cm', length: '79 cm', shoulder: '49 cm' }
        ]
      };
    } else if (category.includes('chaqueta')) {
      return {
        title: 'Guía de Tallas - Chaquetas',
        sizes: [
          { size: 'S', chest: '54 cm', length: '68 cm', shoulder: '45 cm' },
          { size: 'M', chest: '58 cm', length: '71 cm', shoulder: '47 cm' },
          { size: 'L', chest: '62 cm', length: '74 cm', shoulder: '49 cm' },
          { size: 'XL', chest: '66 cm', length: '77 cm', shoulder: '51 cm' }
        ]
      };
    } else if (category.includes('sudadera')) {
      return {
        title: 'Guía de Tallas - Sudaderas',
        sizes: [
          { size: 'S', chest: '52 cm', length: '68 cm', shoulder: '50 cm' },
          { size: 'M', chest: '56 cm', length: '71 cm', shoulder: '52 cm' },
          { size: 'L', chest: '60 cm', length: '74 cm', shoulder: '54 cm' },
          { size: 'XL', chest: '64 cm', length: '77 cm', shoulder: '56 cm' }
        ]
      };
    } else if (category.includes('chaleco')) {
      return {
        title: 'Guía de Tallas - Chalecos',
        sizes: [
          { size: 'S', chest: '52 cm', length: '66 cm', shoulder: '42 cm' },
          { size: 'M', chest: '56 cm', length: '69 cm', shoulder: '44 cm' },
          { size: 'L', chest: '60 cm', length: '72 cm', shoulder: '46 cm' },
          { size: 'XL', chest: '64 cm', length: '75 cm', shoulder: '48 cm' }
        ]
      };
    }
    
    return {
      title: 'Guía de Tallas',
      sizes: [
        { size: 'S', chest: '46-52 cm', length: '66-70 cm', shoulder: '42-45 cm' },
        { size: 'M', chest: '52-56 cm', length: '70-73 cm', shoulder: '44-47 cm' },
        { size: 'L', chest: '56-62 cm', length: '73-76 cm', shoulder: '46-49 cm' },
        { size: 'XL', chest: '62-66 cm', length: '76-79 cm', shoulder: '48-51 cm' }
      ]
    };
  };

  const sizeGuide = getSizeGuide();

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <Link to="/catalogo" className="text-orange-600 hover:text-orange-700 font-medium text-sm cursor-pointer inline-flex items-center">
              <i className="ri-arrow-left-line mr-2"></i>
              Volver al Catálogo
            </Link>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 p-8 lg:p-12">
              <div>
                <div className="relative rounded-xl overflow-hidden bg-gray-50 mb-6">
                  <div className="w-full h-[600px]">
                    <img 
                      src={getColorImage(selectedColor)} 
                      alt={`${product.name} - ${selectedColor}`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  {product.featured && (
                    <div className="absolute top-4 right-4 bg-yellow-400 text-gray-900 px-4 py-2 rounded-full text-sm font-semibold">
                      Destacado
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-4 gap-4">
                  {product.colors.slice(0, 4).map((color) => (
                    <div 
                      key={color}
                      onClick={() => setSelectedColor(color)}
                      className={`aspect-square rounded-lg overflow-hidden bg-gray-100 cursor-pointer border-2 transition-all ${
                        selectedColor === color ? 'border-orange-500 shadow-lg' : 'border-transparent hover:border-gray-300'
                      }`}
                    >
                      <div className="w-full h-full">
                        <img 
                          src={getColorImage(color)} 
                          alt={`${product.name} - ${color}`}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="mb-6">
                  <p className="text-sm text-gray-500 uppercase tracking-wide mb-2">
                    {product.category}
                  </p>
                  <h1 className="text-4xl font-bold text-gray-900 mb-4">
                    {product.name}
                  </h1>
                  <p className="text-gray-600 leading-relaxed">
                    {product.description}
                  </p>
                </div>

                <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 mb-6">
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Precio por unidad</p>
                      <p className="text-5xl font-bold text-orange-600">
                        {product.unitPrice.toFixed(2)}€
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600 mb-1">Pedido mínimo</p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {product.minQuantity} uds
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Color - {selectedColor}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {product.colors.map((color) => (
                      <button
                        key={color}
                        onClick={() => setSelectedColor(color)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                          selectedColor === color
                            ? 'bg-orange-600 text-white shadow-md'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {color}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-semibold text-gray-900">
                      Talla
                    </label>
                    <button
                      onClick={() => setShowSizeGuide(!showSizeGuide)}
                      className="text-sm text-orange-600 hover:text-orange-700 font-medium cursor-pointer inline-flex items-center"
                    >
                      <i className="ri-ruler-line mr-1"></i>
                      Guía de Tallas
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {product.sizes.map((size) => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                          selectedSize === size
                            ? 'bg-orange-600 text-white shadow-md'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>

                {showSizeGuide && (
                  <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                      <i className="ri-ruler-line mr-2 text-blue-600"></i>
                      {sizeGuide.title}
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-blue-200">
                            <th className="text-left py-2 px-3 font-semibold text-gray-900">Talla</th>
                            <th className="text-left py-2 px-3 font-semibold text-gray-900">Pecho (ancho)</th>
                            <th className="text-left py-2 px-3 font-semibold text-gray-900">Largo</th>
                            <th className="text-left py-2 px-3 font-semibold text-gray-900">Hombro</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sizeGuide.sizes.map((item) => (
                            <tr key={item.size} className="border-b border-blue-100">
                              <td className="py-2 px-3 font-semibold text-gray-900">{item.size}</td>
                              <td className="py-2 px-3 text-gray-700">{item.chest}</td>
                              <td className="py-2 px-3 text-gray-700">{item.length}</td>
                              <td className="py-2 px-3 text-gray-700">{item.shoulder}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-gray-600 mt-3">
                      <i className="ri-information-line mr-1"></i>
                      Medidas aproximadas. Pueden variar ligeramente según el modelo.
                    </p>
                  </div>
                )}

                <div className="mb-8">
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Cantidad (mínimo {product.minQuantity} unidades)
                  </label>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => setQuantity(Math.max(product.minQuantity, quantity - 5))}
                      className="w-12 h-12 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center cursor-pointer transition-colors"
                    >
                      <i className="ri-subtract-line text-xl"></i>
                    </button>
                    <input
                      type="number"
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(product.minQuantity, parseInt(e.target.value) || product.minQuantity))}
                      min={product.minQuantity}
                      className="w-24 h-12 text-center text-xl font-semibold border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                    <button
                      onClick={() => setQuantity(quantity + 5)}
                      className="w-12 h-12 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center cursor-pointer transition-colors"
                    >
                      <i className="ri-add-line text-xl"></i>
                    </button>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6 mb-8">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="text-xl font-semibold text-gray-900">{totalPrice}€</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Envío:</span>
                    <span className="text-xl font-semibold text-green-600">GRATIS</span>
                  </div>
                  <div className="border-t border-gray-200 mt-4 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-gray-900">Total:</span>
                      <span className="text-3xl font-bold text-orange-600">{totalPrice}€</span>
                    </div>
                  </div>
                </div>

                <Button 
                  fullWidth 
                  size="lg"
                  onClick={handleAddToCart}
                >
                  Añadir al Carrito
                  <i className="ri-shopping-cart-line ml-2"></i>
                </Button>

                <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700">
                    <i className="ri-information-line text-yellow-600 mr-2"></i>
                    <strong>Proceso de pedido:</strong> Una vez realizado el pedido, confirmaremos disponibilidad en 24h. Después recibirás la factura para realizar el pago por transferencia o Bizum.
                  </p>
                </div>

                <div className="mt-8 grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <i className="ri-truck-line text-2xl text-orange-600 mb-2"></i>
                    <p className="text-xs text-gray-600">Envío Gratis</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <i className="ri-shield-check-line text-2xl text-orange-600 mb-2"></i>
                    <p className="text-xs text-gray-600">Calidad Premium</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <i className="ri-time-line text-2xl text-orange-600 mb-2"></i>
                    <p className="text-xs text-gray-600">Envío 24-48h</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Características del Producto</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-3">
                <i className="ri-checkbox-circle-fill text-orange-500 text-xl mt-1"></i>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Alta Calidad</h3>
                  <p className="text-sm text-gray-600">Materiales premium de las mejores marcas</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <i className="ri-checkbox-circle-fill text-orange-500 text-xl mt-1"></i>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Personalizable</h3>
                  <p className="text-sm text-gray-600">Perfecto para serigrafía, bordado y transfer</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <i className="ri-checkbox-circle-fill text-orange-500 text-xl mt-1"></i>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Stock Permanente</h3>
                  <p className="text-sm text-gray-600">Disponibilidad inmediata de todos los colores</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <i className="ri-checkbox-circle-fill text-orange-500 text-xl mt-1"></i>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Envío Rápido</h3>
                  <p className="text-sm text-gray-600">Procesamos tu pedido en 24-48 horas</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}